import { CatalogItem } from '../../domain/catalogItem'
import { Item, Job, QuantityChange } from "./editVisitApi"

export type WorkareaItemChanges = {
  workareaId: number
  added: Item[]
  quantityChanges: QuantityChange[]
}

export type EnrichedItem = Item & {
  key: string
  quantityChanged: boolean
  canBeRemoved: boolean
}

export type EnrichedWorkareaItems = {
  workareaId: number
  items: EnrichedItem[]
}

const updateOrAddToArray = <T>(array: T[], match: (x: T) => boolean, update: (x: T) => T, add: () => T): T[] => {
  if (array.some(match)) {
    return array.map(x => match(x) ? update(x) : { ...x })
  }
  return [...array, add()]
}

const updateWorkarea = (state: WorkareaItemChanges[], workareaId: number, updater: (x: WorkareaItemChanges) => WorkareaItemChanges) => {
  return updateOrAddToArray(state, x => x.workareaId === workareaId, updater, () => updater({
    workareaId,
    quantityChanges: [],
    added: []
  }))
}

export const addItem = (state: WorkareaItemChanges[], workareaId: number, item: CatalogItem) => {
  return updateWorkarea(state, workareaId, x => ({
    ...x,
    added: x.added.some(y => y.catalogItemGuid === item.guid)
      ? x.added
      : [...x.added, { catalogItemGuid: item.guid, name: item.name, units: item.units ?? '', estimatedQty: 0, visitActualQty: 0, jobActualQty: 0 }]
  }))
}

const matchItem = (match: QuantityChange | Item) => (x: QuantityChange): boolean => {
  if (match.actualItemGuid) {
    return x.actualItemGuid === match.actualItemGuid
  }
  if (match.estimatedItemGuid) {
    return x.estimatedItemGuid === match.estimatedItemGuid
  }
  if (match.copyActualItemGuid) {
    return x.copyActualItemGuid === match.copyActualItemGuid
  }
  return x.catalogItemGuid === match.catalogItemGuid
}

export const changeQuantity = (state: WorkareaItemChanges[], workareaId: number, quantityChange: QuantityChange) => {
  return updateWorkarea(state, workareaId, x => ({
    ...x,
    quantityChanges: updateOrAddToArray(x.quantityChanges, matchItem(quantityChange), () => quantityChange, () => quantityChange)
  }))
}

export const removeItem = (state: WorkareaItemChanges[], workareaId: number, catalogItemGuid: string) => {
  return updateWorkarea(state, workareaId, x => {
    const quantityChanges = x.quantityChanges.filter(x => x.catalogItemGuid !== catalogItemGuid)
    return { ...x, quantityChanges, added: x.added.filter(y => y.catalogItemGuid !== catalogItemGuid) }
  })
}

export const createEnrichedItemKey = (item: Item): string => `${item.catalogItemGuid}|${item.estimatedItemGuid ?? ''}|${item.actualItemGuid ?? ''}|${item.copyActualItemGuid ?? ''}`

const enrichItem = (item: Item, changesWorkarea: WorkareaItemChanges, isAdded: boolean): EnrichedItem => {
  const change = changesWorkarea.quantityChanges.find(matchItem(item))
  const diff = change ? change.quantity - item.visitActualQty : 0

  return {
    ...item,
    key: createEnrichedItemKey(item),
    visitActualQty: item.visitActualQty + diff,
    jobActualQty: item.jobActualQty + diff,
    quantityChanged: !isAdded && diff !== 0,
    canBeRemoved: isAdded
  }
}

export const enrichWorkareaItems = (job: Job, state: WorkareaItemChanges[]): EnrichedWorkareaItems[] => {
  const allWorkareaIds = new Set([
    ...job.workareas.map(x => x.id),
    ...state.map(x => x.workareaId)
  ])
  return Array.from(allWorkareaIds).map(workareaId => {
    const jobWorkarea = job.workareas.find(x => x.id === workareaId) || { workareaId, items: [] }
    const changesWorkarea = state.find(x => x.workareaId === workareaId) || { workareaId, added: [], quantityChanges: [], removedItemIds: [] }
    const uniqueAdditions = changesWorkarea.added.filter(x => !jobWorkarea.items.some(y => y.catalogItemGuid === x.catalogItemGuid))
    return {
      workareaId,
      items: [
        ...jobWorkarea.items.map(item => enrichItem(item, changesWorkarea, false)),
        ...uniqueAdditions.map(item => enrichItem(item, changesWorkarea, true))
      ]
    }
  })
}
